import { Box } from '@mui/material';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import SimpleBar from 'simplebar-react';
import { Questionaire } from 'src/common/keyText';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';

const rowStyle = {
  '&:last-child td, &:last-child th': { border: 0 },
  '&.MuiTableRow-root .MuiTableCell-root.MuiTableCell-body': {
    py: '16px',
    borderBottom: '1px solid #F3F3F3',
  },
};

const tableStyle = {
  minWidth: 470,
  '& .MuiTableBody-root th': { fontWeight: 700 },
  '& .MuiTableCell-root.MuiTableCell-head': {
    borderBottom: '1px solid #F3F3F3',
    fontWeight: 700,
    p: '6px 16px',
    fontSize: '14px',
  },
  '& .MuiTableCell-root.MuiTableCell-body': {
    color: 'primary.dark',
    fontSize: '14px',
    py: '6px',
  },
};

type DetailAnswerType = {
  answer: any;
  patientid: string | null;
  handleUpdateClick: () => void;
};

const DetailAnswer: React.FC<DetailAnswerType> = (props) => {
  const notifyContext = useNotify();
  const printRefDetail = useRef(null);

  const handlePrintAnswer = () => {
    if (!props.answer) {
      notifyContext.setNotifyMessage({
        show: true,
        message: '入力データがありません。WEB問診表を入力してください。',
        type: 'error'
      });
      return;
    }
    printAnswer();
  };

  const printAnswer = useReactToPrint({
    content: () => printRefDetail.current,
  });

  const tableDetail = () => {
    return (
      <Stack spacing={'20px'} width={'100%'}>
        {!props.answer && <Typography color={'#A3AED0'}>入力データはありません</Typography>}
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            width: '100%',
            height: '100%',
          }}
        >
          <Table sx={tableStyle} aria-label='simple table'>
            <TableBody>
              {props.answer?.answers &&
                Object.entries(props.answer?.answers).map(
                  (ans: any, index: number) => {
                    return (
                      <TableRow sx={rowStyle} key={index}>
                        <TableCell component={'th'} width={'50%'} sx={{ wordBreak: 'break-all' }}>
                          {ans[0]}
                        </TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>
                          {Array.isArray(ans[1]['value'])
                            ? ans[1]['value'].join('、')
                            : ans[1]['value']}
                          {ans[1]['children_answers'] &&
                            Object.entries(ans[1]['children_answers']).map(
                              (chil_ans: any, childIdx: number) => {
                                return (
                                  <div key={childIdx}>
                                    {/* {chil_ans[1]['value'].length > 1 ? } */}
                                    {typeof chil_ans[1][
                                      'dependent_answers'
                                    ][0] === 'object' ? (
                                      Array.isArray(chil_ans[1]['value']) &&
                                      chil_ans[1]['value'].map(
                                        (
                                          chilAnsValue: any,
                                          daIndex: number
                                        ) => {
                                          if (chilAnsValue.startsWith('other_more_information: ')) {
                                            return <div key={daIndex}></div>;
                                          }
                                          if (chilAnsValue.startsWith('その他')) {
                                            const infoEntry = chil_ans[1]['value'].find(
                                              (item: string) => item.startsWith('other_more_information: ')
                                            );
                                            
                                            if (infoEntry) {
                                              const additionalInfo = infoEntry.replace('other_more_information: ', '');
                                              return (
                                                <div key={daIndex}>
                                                  <div>
                                                    {`${chilAnsValue}`}:{' '}
                                                    {chil_ans[1][
                                                      'dependent_answers'
                                                    ][0][chilAnsValue] || ''} {`（${additionalInfo}）`}
                                                  </div>
                                                </div>
                                              );
                                            }
                                          }
                                          return (
                                            <div key={daIndex}>
                                              {chilAnsValue}:{' '}
                                              {chil_ans[1][
                                                'dependent_answers'
                                              ][0][chilAnsValue] || ''}
                                            </div>
                                          );
                                        }
                                      )
                                    ) : (
                                      <>
                                        {chil_ans[1]['value'].join('、')}
                                        {chil_ans[1]['dependent_answers'][0]
                                          ? `（${chil_ans[1][
                                              'dependent_answers'
                                            ].join('、')}）`
                                          : ''}
                                      </>
                                    )}
                                  </div>
                                );
                              }
                            )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  return (
    <Stack
      flex={1}
      sx={{
        '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
          backgroundColor: '#CCD1D9',
        },
      }}
    >
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignContent={'center'}
        my={'8px'}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={'8px'}>
          <Typography
            fontSize={'20px'}
            fontWeight={500}
            color={'primary.dark'}
            my={'8px'}
            height={'40px'}
            lineHeight={'40px'}
          >
            {Questionaire.Title}
          </Typography>
          <BtnOutlineGradient
            text='変更'
            handleButtonClick={props.handleUpdateClick}
            width='70px'
          />
        </Stack>
        <Box
          component={'img'}
          src='/images/ic-print.svg'
          alt='ic-print'
          width={'40px'}
          sx={{ cursor: 'pointer', p: '8px' }}
          onClick={handlePrintAnswer}
        />
      </Stack>
      <Typography
        fontSize={'14px'}
        fontWeight={500}
        mb={'8px'}
        color={'#0000008a'}
      >
        入力日時: {props.answer?.submitted_at}
      </Typography>
      <SimpleBar style={{ maxHeight: 'calc(100vh - 217px)', width: '100%' }}>
        {tableDetail()}
      </SimpleBar>
      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefDetail}>
          <Stack justifyContent={'center'} alignItems={'center'} py={'16px'}>
            <Typography fontSize={'20px'} fontWeight={700}>
              {Questionaire.Title}
            </Typography>
          </Stack>
          <Stack justifyContent={'center'} py={'4px'} pl={'16px'}>
            <Typography fontSize={'14px'}>
              患者名：{props.answer?.patient_name}
            </Typography>
            <Typography fontSize={'14px'}>
              患者番号：{props.answer?.patient_no}
            </Typography>
            <Typography fontSize={'14px'}>
              入力日時：{props.answer?.submitted_at}
            </Typography>
          </Stack>
          {tableDetail()}
        </Box>
      </Box>
    </Stack>
  );
};
export default DetailAnswer;
